//REACT AND REACT BASED LIBRARIES
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";
//COMPONENTS
import Table from "@components/Table";
import { Box, Container } from "@mui/material";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import BackgroundImage from "@components/BackgroundImageCst";
import CustomRowAction from "@components/Table/components/CustomRowAction";
//CUSTOM HOOKS AND UTILS
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL, uploadFile } from "@utils/utilsApi";
import { useFetchMock } from "src/mock_data/useMockData";
import { MethodType } from "src/services/type";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { ActionsType } from "src/interfaces/Common";
import { useFetchTable } from "@hooks/useFetchDataTable";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ButtonBackPage from "@components/ButtonBackPage";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import ModalDownloadUploadMultilanguageCst from "../../_components/ModalDownloadUploadMultilanguageCst";
import useModalMultilanguage from "../../hooks/useModalMultilanguage";
import { Tour } from "@mui/icons-material";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

//LEGENDA
// !! IMPORTANTE/ CORE PAGINA
// ?? FACOLTATIVO/ DIPENDE DALLA NECESSITÀ
// ## USATO PER DATI MOCKATI, NON NECESSARIO UNA VOLTA RICEVUTI I SERVIZI BE

const Jobs: React.FC = () => {
  //!! HOOK PER GESTIRE TRADUZIONI
  const { t } = useTranslation(["skills-jobs-page"]); //NECESSARIO AGGIORNARE FILES DI TRADUZIONE
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["skills.jobs"]}`;

  //?? HOOK PER PARAMETRI DA URL (IL NOME è LO STESSO DI QUELLO INSERITO IN ROUTER es. /:idLO)
  const { id, IdProfession } = useParams();

  const { selectOption, dataCorporate } = useModalMultilanguage();
  const [openMultilanguageModal, setOpenMultilanguageModal] = useState(false);

  //!! STATO PER GESTIONE MODALE DI CONFERMA/RISPOSTA/ERRORE
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  //?? STATI PER GESTIRE LA CANCELLAZIONE e MODALE
  const [idElementDeleted, setIdElementDeleted] = useState<string>();
  const [openModalDelete, setOpenModalDelete] = useState(false);

  //!! CHIAMATA GET
  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getJobs
        .replace(":idCorporate", id ? id : "1")
        .replace(":idProfession", IdProfession || "")}`, //!! (SOLO DI ESEMPIO) MODIFICARE SERVICE URL CON QUELLO INSERITO NEL FILE DI CONFIG
      PagesID["skills.jobs"], //!! (SOLO DI ESEMPIO) MODIFICARE PAGE ID CON QUELLO INSERITO NEL FILE DI CONFIG
      filters[filterId],
    );
  const { trigger: triggerObsolete } = useSWRMutation(
    `${ServicesURL.putJobsObsolete
      .replace(":idCorporate", id ? id : "1")
      .replace(":idProfession", IdProfession || "")}`, //!! (SOLO DI ESEMPIO) MODIFICARE SERVICE URL CON QUELLO INSERITO NEL FILE DI CONFIG
    (url: string, { arg }: { arg: { id: string; obsolete: boolean } }) => {
      return fetcher<any>(`${url.replace(":idJobs", arg.id)}`, MethodType.PUT, {
        obsolete: !arg.obsolete,
      });
    },
  );

  const { trigger: triggerDelete } = useSWRMutation(
    `${ServicesURL.putJobs
      .replace(":idCorporate", id ? id : "1")
      .replace(":idProfession", IdProfession || "")}/${idElementDeleted}`,
    (url: string) => fetcher(`${url}`, MethodType.DELETE),
  );

  const handleObsolete = async ({
    id,
    obsolete,
  }: {
    id: string;
    obsolete: boolean;
  }) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });

    try {
      await triggerObsolete({ id, obsolete });
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
      console.error(error);
    }
    mutate && mutate();
  };

  const handleDelete = React.useCallback(async () => {
    if (idElementDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDelete();
        const newData = [
          ...dataTable.output.filter((el: any) => el.id !== idElementDeleted),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdElementDeleted(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idElementDeleted, mutate, triggerDelete]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "delete":
          return {
            ...action,
            callBack: (id: any) => setIdElementDeleted(id),
          };
        case "button":
          return {
            ...action,
            callBack: handleObsolete,
            getIcon: (row: any) => {
              if (row.obsolete) {
                return <LockIcon />;
              } else {
                return <LockOpenIcon />;
              }
            },
          };
        default:
          return action;
      }
    });
  }, [actionForRow]);

  //FUNZIONE NECESSARIA PER APPORTARE MODIFICHE AI CAMPI DELLE AZIONI DI RIGA MA IN BASE AL VALORE DEI CAMPI (ROW)
  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    //FUNZIONE PER ABILITAZIONE/ DISABILITAZIONE CAMPI IN BASE A VALORI DELLA ROW
    // configRowNew = disableRowBasedOnDisableIf(configRowNew, row);

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };
  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-skills-jobs.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${t("hero-title")}`}
          description={t("hero-description")}
        />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={PagesID["skills.jobs"]}
            data={dataTable?.output || []}
            columns={columns || []}
            configurationTopToolbar={actionsTopToolbar?.map((action: any) => {
              switch (action?.icon) {
                case "multilanguage":
                  return {
                    ...action,
                    callBack: () => {
                      setOpenMultilanguageModal(true);
                    },
                    getIcon: () => <Tour />,
                  };
                default:
                  return action;
              }
            })}
            configurationActionForRow={configurationActionForRow}
            renderRowActions={customRenderRowActions}
            totalPages={dataTable?.metadata?.total_elements}
            modalTitle={t("hero-title")?.replace("-", "") || ""}
          />
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!idElementDeleted}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setIdElementDeleted(undefined);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
      />
      <ModalDownloadUploadMultilanguageCst
        open={!!openMultilanguageModal}
        setOpen={() => {
          setOpenMultilanguageModal(false);
        }}
        selectOption={selectOption}
        type={"JOB"}
      />
    </AnimationFadeIn>
  );
};

export default Jobs;
